import {Alert, Box, Button, Stack} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTablePrint,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../../../common/serverTable";
import {FOLIO_STATUS_LABEL_KEYS, FolioStatusElement} from "../../../../common/uiElements/FolioStatusElement";
import DigitalSigningDetailsDialog from "../../../../digitalSigning/details/DigitalSigningDetailsDialog";
import {isSignedDigitalSigning} from "../../../../digitalSigning/share/digitalSigningFlow.util";
import DigitalSigningSignatureButton from "../../../../digitalSigning/share/DigitalSigningSignatureButton";
import {useLatestFolioDigitalSigning} from "../../../../digitalSigning/share/useLatestDigitalSigning";
import {FolioFormat} from "./folioFormat";
import {FolioListEntry, FolioListLabels, folioTotalCents, listFolioEntries} from "./folioList.util";
import {FOLIO_DIALOG_TABLE_ID} from "./folioQueryKeys";
import {fetchFolioList, folioListKey, FolioListScope, useFolioList, useFolioName} from "./useFolioList";

const PAGE_SIZE = 10;
const TEXT_SX = {overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'};

interface NameCellProps {
    entry: FolioListEntry
    /** Shows what the guest signed, by the signing's id. */
    onShowSigning: (signingId: string) => void
}

// The folio's name, then the green icon once the guest signed - a draft with it waits for Confirm
// and charge. Its own component, subscribed to the folio's latest signing (the dialog has read it
// ahead): the columns stay as they are
const FolioNameCell = ({entry: {folio, name}, onShowSigning}: NameCellProps) => {
    const {data: latest} = useLatestFolioDigitalSigning(folio);
    const signed = isSignedDigitalSigning(latest?.status) ? latest : undefined;

    // The name gives way (its ellipsis), never the icon - an IconButton doesn't shrink; a button's click
    // never opens the row's folio
    return (
        <Stack direction={'row'} alignItems={'center'} gap={0.5}>
            <Box sx={TEXT_SX}>{name}</Box>
            {!!signed && (
                <DigitalSigningSignatureButton signing={signed} size={'small'} onClick={() => onShowSigning(signed.id)}/>
            )}
        </Stack>
    );
};

interface Props {
    scope: FolioListScope
    format: FolioFormat
    /** Saving or creating: no folio opens, none is created meanwhile. */
    busy: boolean
    /** Read-only users create nothing, nor does a folio without a reservation or group. */
    canCreate: boolean
    onOpen: (folioId: string) => void
    onCreate: () => void
    /** Closes the dialog: what the guest signed opens over the list, and its X closes both. */
    onClose: () => void
}

/**
 * The reservation's folios (a package's member's with the package's) in the app's table, embedded,
 * under its toolbar: search, sort, columns, print and "Create folio" - the dialog's header refreshes
 * them. Folio · Status · Total · Last updated, the ID to add - each sortable, oldest first until a
 * sort is chosen. The dialog reads all of them at once (they are few); the table searches, sorts and
 * pages that list here. A row opens its folio - but the green icon after the name of one the guest
 * signed shows what they signed.
 */
const FolioListView = ({scope, format, busy, canCreate, onOpen, onCreate, onClose}: Props) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const folioName = useFolioName();
    const {reservationId, reservationGroupId} = scope;
    // All of them, oldest first - what the table pages; the header's Refresh reads them again
    const listQuery = useFolioList(scope, true);
    const folios = listQuery.data;
    // What the guest signed, over the list
    const [shownSigningId, setShownSigningId] = useState<string>();

    const labels = useMemo<FolioListLabels>(() => ({
        name: folioName,
        status: status => (status ? t(FOLIO_STATUS_LABEL_KEYS[status] ?? status) : ''),
    }), [folioName, t]);

    // isServerSortable is the table's flag for a sortable column: the query below sorts them
    const columns = useMemo<ColumnDef<FolioListEntry>[]>(() => [
        {
            id: 'name',
            header: t('Folio'),
            accessorFn: entry => entry.name,
            // "Folio 2" by its place among all of them, as the header counts - never by its row, so a
            // sort or a search renames nothing
            cell: ({row}) => <FolioNameCell entry={row.original} onShowSigning={setShownSigningId}/>,
            size: 160,
            meta: {
                grow: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                // The name alone: the icon is for the screen
                printOriginalCell: row => <Box sx={TEXT_SX}>{row.original.name}</Box>,
            },
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: entry => entry.statusLabel,
            cell: ({row}) => <FolioStatusElement status={row.original.folio.status}/>,
            size: 140,
            meta: {isServerSortable: true, printable: true, printDefault: true},
        },
        {
            id: 'total',
            header: t('Total'),
            accessorFn: entry => folioTotalCents(entry.folio),
            cell: ({row}) => (
                <Box sx={{...TEXT_SX, fontVariantNumeric: 'tabular-nums'}}>{format.money(folioTotalCents(row.original.folio))}</Box>
            ),
            size: 132,
            meta: {
                align: 'right',
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => format.money(folioTotalCents(row.original.folio)),
            },
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: entry => entry.folio.id,
            cell: ({row}) => <Box sx={TEXT_SX}>{row.original.folio.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true, printable: true},
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: entry => entry.folio.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.folio.updateDate)}</Box>,
            size: 200,
            meta: {
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => formatFullDateTime(row.original.folio.updateDate),
            },
        },
    ], [format, t]);

    const controller = useServerTable<FolioListEntry>({
        tableId: FOLIO_DIALOG_TABLE_ID,
        presentation: 'embedded',
        defaultPageSize: PAGE_SIZE,
        columns,
        getRowId: entry => entry.folio.id!,
        onRowClick: entry => {
            if (!busy && entry.folio.id) onOpen(entry.folio.id);
        },
        queryDependencies: [reservationId, reservationGroupId],
        // Printed whole, not a page of it
        allRows: {},
        // Never the backend's search: it is a guest's name, and replaces the reservation's folios with
        // everyone's. Oldest first until a sort is chosen: sorted here, not through `initialSorting`,
        // which would put a remove-sort control in the header
        queryFn: async ({pagination: {pageIndex, pageSize}, searchQuery, sorting}) => {
            // Without a reservation or group there is nothing to list - never the organization's folios
            if (!reservationId && !reservationGroupId) return {content: [], totalElements: 0};
            // The dialog's list as read - its first read joined -, read again only once invalidated
            const all = await queryClient.fetchQuery(
                folioListKey({reservationId, reservationGroupId}),
                () => fetchFolioList({reservationId, reservationGroupId}),
                {staleTime: Infinity}
            );
            const entries = listFolioEntries(all, labels, searchQuery, sorting);
            return {content: entries.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize), totalElements: entries.length};
        },
    });

    // The table follows the list: each new read of it (the header's Refresh, a folio created or saved)
    // makes the page again
    useEffect(() => {
        if (listQuery.dataUpdatedAt) void controller.query.refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listQuery.dataUpdatedAt]);

    const retry = () => void listQuery.refetch();
    // The list's last read failed - a Refresh's too: its rows may be old -, until one succeeds. Unlike
    // isError it holds while a read is tried again, so the warning doesn't flicker
    const failed = controller.query.isError || listQuery.errorUpdatedAt > listQuery.dataUpdatedAt;

    // While the table reads its first page, as many placeholder rows as the reservation has folios
    // (when the dialog's list knows them), not ten: the dialog keeps the height it opened at
    const placeholderRows = Math.min(Math.max(folios?.length ?? 1, 1), PAGE_SIZE);
    const placeholderSx = controller.query.isLoading
        ? {[`& tbody > tr:nth-of-type(n+${placeholderRows + 1})`]: {display: 'none'}}
        : undefined;

    return (
        <Box sx={{flex: '1 1 auto', minHeight: 0, minWidth: 0, overflowY: 'auto', px: {xs: 2, md: 3}, py: 2}}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableColumns/>
                        <ServerTablePrint title={t('Folios')}/>
                        <ServerTableCreate label={t('Create folio')} onClick={onCreate} disabled={busy || !canCreate}/>
                    </ServerTableToolbar>

                    {/* The table's own error row has no Retry: this warning takes its place */}
                    {failed ? (
                        <Alert
                            severity={'warning'}
                            action={<Button color={'inherit'} size={'small'} onClick={retry}>{t('Retry')}</Button>}
                        >
                            {t('The folios could not be loaded.')}
                        </Alert>
                    ) : (
                        // Where the dialog is narrower than the columns (a phone), only the table scrolls sideways
                        <Box sx={{overflowX: 'auto', ...placeholderSx}}>
                            <ServerTable/>
                        </Box>
                    )}
                </ServerTableFrame>
            </ServerTableProvider>

            {/* What the guest signed, as a view of the list: its arrow leads back to all folios, its X
                closes both. Out of the table, so no click in it reaches a row */}
            {!!shownSigningId && (
                <DigitalSigningDetailsDialog
                    id={shownSigningId}
                    open
                    backLabel={t('All folios')}
                    onBack={() => setShownSigningId(undefined)}
                    onClose={() => {
                        setShownSigningId(undefined);
                        onClose();
                    }}
                />
            )}
        </Box>
    );
};

export default FolioListView;
